import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from 'react-native';

import {
  SavedAttachment,
  getAttachment,
  getAttachmentThumbnail,
  getViewableFileUrl,
} from '../../../services/attachmentService';
import { Check, X, ImageIcon, ChevronLeft, ChevronRight } from '../../icons';

import type { DocumentFormat } from '../../../types/pdf';

/**
 * PDF page layouts, written `columns×rows`:
 *   - 1×1 → one image per page
 *   - 1×2 → two images stacked (1 column, 2 rows)
 *   - 2×1 → two images side-by-side (2 columns, 1 row)
 *   - 2×2 → a 2×2 grid (four images per page)
 */
export interface PageLayout {
  key: string;
  columns: number;
  rows: number;
}

const LAYOUTS: PageLayout[] = [
  { key: '1x1', columns: 1, rows: 1 },
  { key: '1x2', columns: 1, rows: 2 },
  { key: '2x1', columns: 2, rows: 1 },
  { key: '2x2', columns: 2, rows: 2 },
];

/** Default: two stacked images per page (matches the prior default of 2). */
const DEFAULT_LAYOUT_KEY = '1x2';

/** How many image rows the (paginated) List shows at a time. */
const LIST_PAGE_SIZE = 5;

export interface ImageSelectionResult {
  includeAll: boolean;
  selectedIds: number[];
  /** Grid columns for the PDF page layout (1 or 2). */
  columns: number;
  /** Grid rows for the PDF page layout (1 or 2). */
  rows: number;
  /** Output file format — 'pdf' (default) or 'docx' (Word) (#1390). */
  format: DocumentFormat;
}

interface ImageSelectionModalProps {
  visible: boolean;
  /** Pre-filtered image attachments for the request. */
  images: SavedAttachment[];
  onCancel: () => void;
  onConfirm: (result: ImageSelectionResult) => void;
}

/** Human-readable file size (e.g. "3.2 MB", "940 KB"). */
function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Lazily load a preview data-url for an image attachment: prefer a stored
 * thumbnail, fall back to the full image.
 */
function useImagePreview(image: SavedAttachment): {
  uri: string | null;
  loading: boolean;
} {
  const [uri, setUri] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        let url = await getAttachmentThumbnail(image.id);
        if (!url) {
          const result = await getAttachment(image.id);
          if (result.success && result.attachment?.attachmentFile) {
            url = getViewableFileUrl(
              result.attachment.attachmentFile,
              result.attachment.mimeType || 'image/jpeg',
            );
          }
        }
        if (active) setUri(url);
      } catch {
        if (active) setUri(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [image.id]);

  return { uri, loading };
}

/**
 * #1340 — lets the user choose which image attachments to embed in the Summary
 * PDF and the per-page grid layout, then export. Cross-platform via react-native
 * `Modal` (web + native).
 *
 * Note: uses local, permission-free checkboxes (not `FormCheckbox`) because image
 * selection is a report option available to view-only users too, whereas
 * `FormCheckbox` gates on request-edit permissions.
 */
export const ImageSelectionModal: React.FC<ImageSelectionModalProps> = ({
  visible,
  images,
  onCancel,
  onConfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [layoutKey, setLayoutKey] = useState<string>(DEFAULT_LAYOUT_KEY);
  const [listPage, setListPage] = useState(0);
  const [format, setFormat] = useState<DocumentFormat>('pdf');

  // Reset to defaults each time the modal opens.
  useEffect(() => {
    if (visible) {
      setSelectedIds(new Set(images.map(img => img.id)));
      setLayoutKey(DEFAULT_LAYOUT_KEY);
      setListPage(0);
      setFormat('pdf');
    }
  }, [visible, images]);

  const allSelected = images.length > 0 && selectedIds.size === images.length;
  const noneSelected = selectedIds.size === 0;
  const masterIndeterminate = !allSelected && !noneSelected;

  const layout = LAYOUTS.find(l => l.key === layoutKey) ?? LAYOUTS[0];

  // List pagination (browsing the selection UI — independent of the PDF grid).
  const listPageCount = Math.max(1, Math.ceil(images.length / LIST_PAGE_SIZE));
  const safeListPage = Math.min(listPage, listPageCount - 1);
  const visibleImages = useMemo(
    () =>
      images.slice(
        safeListPage * LIST_PAGE_SIZE,
        safeListPage * LIST_PAGE_SIZE + LIST_PAGE_SIZE,
      ),
    [images, safeListPage],
  );

  const toggleImage = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev =>
      prev.size === images.length ? new Set() : new Set(images.map(i => i.id)),
    );
  };

  const handleExport = () => {
    onConfirm({
      includeAll: allSelected,
      selectedIds: Array.from(selectedIds),
      columns: layout.columns,
      rows: layout.rows,
      format,
    });
  };

  // On Android the overlay's Yoga height can grow to several screens, and
  // justifyContent:'center' then places the card below the visible window.
  // Pin the scrim to the real window so maxHeight percentages resolve inside it.
  const windowSize = useWindowDimensions();
  const overlayFrame =
    Platform.OS === 'android' && windowSize.width > 0 && windowSize.height > 0
      ? {
          width: windowSize.width,
          height: windowSize.height,
          maxHeight: windowSize.height,
        }
      : null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      {...(Platform.OS === 'android'
        ? { statusBarTranslucent: true, navigationBarTranslucent: true }
        : null)}
      onRequestClose={onCancel}
    >
      <View
        style={[styles.overlay, overlayFrame]}
        testID="image-selection-modal"
      >
        <View style={styles.card}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerText}>
              <Text style={styles.title}>Export report</Text>
              <Text style={styles.subtitle}>
                Choose which images to include and the file format.
              </Text>
            </View>
            <TouchableOpacity
              onPress={onCancel}
              style={styles.closeButton}
              testID="image-selection-cancel-x"
              accessibilityLabel="Close"
            >
              <X size={20} color="#6B7280" />
            </TouchableOpacity>
          </View>

          {/* Page layout */}
          <View style={styles.layoutRow}>
            <Text style={styles.layoutRowLabel}>Page layout</Text>
            <View style={styles.layoutTiles}>
              {LAYOUTS.map(l => (
                <LayoutTile
                  key={l.key}
                  layout={l}
                  active={l.key === layoutKey}
                  onPress={() => setLayoutKey(l.key)}
                />
              ))}
            </View>
          </View>

          {/* List */}
          <SelectRow
            label="Include all images"
            checked={allSelected}
            indeterminate={masterIndeterminate}
            onPress={toggleAll}
            testID="image-selection-include-all"
          />
          <ScrollView
            style={styles.body}
            contentContainerStyle={styles.listContent}
          >
            {images.length === 0 ? (
              <Text style={styles.emptyText} testID="image-selection-empty">
                No images are attached to this request. Choose a file format
                below and export the report.
              </Text>
            ) : (
              visibleImages.map(image => (
                <ListRow
                  key={image.id}
                  image={image}
                  selected={selectedIds.has(image.id)}
                  onToggle={() => toggleImage(image.id)}
                />
              ))
            )}
          </ScrollView>

          {/* List pagination */}
          {listPageCount > 1 && (
            <View style={styles.pagination}>
              <TouchableOpacity
                style={[
                  styles.navArrow,
                  safeListPage === 0 && styles.navArrowDisabled,
                ]}
                onPress={() => setListPage(p => Math.max(0, p - 1))}
                disabled={safeListPage === 0}
                testID="image-list-prev"
                accessibilityLabel="Previous page"
              >
                <ChevronLeft
                  size={18}
                  color={safeListPage === 0 ? '#C0C7D0' : '#6B7280'}
                />
              </TouchableOpacity>

              <Text style={styles.paginationText} testID="image-list-page">
                Page {safeListPage + 1} of {listPageCount}
              </Text>

              <TouchableOpacity
                style={[
                  styles.navArrow,
                  safeListPage >= listPageCount - 1 && styles.navArrowDisabled,
                ]}
                onPress={() =>
                  setListPage(p => Math.min(listPageCount - 1, p + 1))
                }
                disabled={safeListPage >= listPageCount - 1}
                testID="image-list-next"
                accessibilityLabel="Next page"
              >
                <ChevronRight
                  size={18}
                  color={
                    safeListPage >= listPageCount - 1 ? '#C0C7D0' : '#6B7280'
                  }
                />
              </TouchableOpacity>
            </View>
          )}

          {/* File format (#1390) */}
          <View style={styles.formatRow}>
            <Text style={styles.formatLabel}>File format</Text>
            <View style={styles.formatToggle} testID="image-format-toggle">
              <FormatPill
                label="PDF"
                active={format === 'pdf'}
                onPress={() => setFormat('pdf')}
                testID="image-format-pdf"
              />
              <FormatPill
                label="Word"
                active={format === 'docx'}
                onPress={() => setFormat('docx')}
                testID="image-format-word"
              />
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={onCancel}
              testID="image-selection-cancel"
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.exportButton]}
              onPress={handleExport}
              testID="image-selection-export"
            >
              <Text style={styles.exportText}>Export</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/** A selectable mini-grid tile drawing `columns × rows` cells. */
const LayoutTile: React.FC<{
  layout: PageLayout;
  active: boolean;
  onPress: () => void;
}> = ({ layout, active, onPress }) => (
  <TouchableOpacity
    style={[styles.layoutTile, active && styles.layoutTileActive]}
    onPress={onPress}
    testID={`image-layout-${layout.key}`}
    accessibilityRole="radio"
    accessibilityState={{ selected: active }}
    accessibilityLabel={`Layout ${layout.columns} by ${layout.rows}`}
  >
    <View style={styles.layoutGrid}>
      {Array.from({ length: layout.rows }).map((_row, r) => (
        <View key={r} style={styles.layoutGridRow}>
          {Array.from({ length: layout.columns }).map((_col, c) => (
            <View
              key={c}
              style={[styles.layoutCell, active && styles.layoutCellActive]}
            />
          ))}
        </View>
      ))}
    </View>
    <Text style={[styles.layoutLabel, active && styles.layoutLabelActive]}>
      {layout.columns}×{layout.rows}
    </Text>
  </TouchableOpacity>
);

/** Segmented pill for the PDF / Word file-format choice (#1390). */
const FormatPill: React.FC<{
  label: string;
  active: boolean;
  onPress: () => void;
  testID?: string;
}> = ({ label, active, onPress, testID }) => (
  <TouchableOpacity
    style={[styles.formatPill, active && styles.formatPillActive]}
    onPress={onPress}
    testID={testID}
    accessibilityRole="button"
    accessibilityState={{ selected: active }}
    accessibilityLabel={`File format: ${label}`}
  >
    <Text
      style={[styles.formatPillText, active && styles.formatPillTextActive]}
    >
      {label}
    </Text>
  </TouchableOpacity>
);

interface SelectRowProps {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onPress: () => void;
  testID?: string;
}

/** Lightweight, permission-free checkbox row (the "Include all" master). */
const SelectRow: React.FC<SelectRowProps> = ({
  label,
  checked,
  indeterminate = false,
  onPress,
  testID,
}) => (
  <TouchableOpacity
    style={styles.masterRow}
    onPress={onPress}
    testID={testID}
    accessibilityRole="checkbox"
    accessibilityState={{ checked: indeterminate ? 'mixed' : checked }}
    accessibilityLabel={label}
  >
    <Checkbox checked={checked} indeterminate={indeterminate} />
    <Text style={styles.masterLabel}>{label}</Text>
  </TouchableOpacity>
);

const Checkbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  small?: boolean;
}> = ({ checked, indeterminate = false, small }) => (
  <View
    style={[
      small ? styles.checkboxSmall : styles.checkbox,
      (checked || indeterminate) && styles.checkboxActive,
    ]}
  >
    {indeterminate ? (
      <View style={styles.checkboxDash} />
    ) : checked ? (
      <Check size={small ? 12 : 13} color="#fff" strokeWidth={2.5} />
    ) : null}
  </View>
);

/** List row: thumbnail + filename + file size + checkbox. */
const ListRow: React.FC<{
  image: SavedAttachment;
  selected: boolean;
  onToggle: () => void;
}> = ({ image, selected, onToggle }) => {
  const { uri, loading } = useImagePreview(image);
  return (
    <TouchableOpacity
      style={styles.listRow}
      onPress={onToggle}
      testID={`image-selection-tile-${image.id}`}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={image.attachmentName}
    >
      <View style={styles.listThumb}>
        {loading ? (
          <ActivityIndicator size="small" color="#E8490F" />
        ) : uri ? (
          <Image source={{ uri }} style={styles.thumbImg} resizeMode="cover" />
        ) : (
          <ImageIcon size={22} color="#bbb" />
        )}
      </View>
      <View style={styles.listMeta}>
        <Text style={styles.listName} numberOfLines={1}>
          {image.attachmentName}
        </Text>
        {!!formatBytes(image.fileSizeBytes) && (
          <Text style={styles.listSize}>
            {formatBytes(image.fileSizeBytes)}
          </Text>
        )}
      </View>
      <Checkbox checked={selected} />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: 20,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    alignSelf: 'center',
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 520 : 460,
    maxHeight: '86%',
    paddingVertical: 20,
    paddingHorizontal: 22,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerText: { flex: 1 },
  title: { fontSize: 18, fontWeight: '700', color: '#1A1A1A' },
  subtitle: { fontSize: 13, color: '#6B7280', marginTop: 3 },
  closeButton: { padding: 4 },

  layoutRow: {
    backgroundColor: '#F9FAFB',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 16,
    marginBottom: 12,
  },
  layoutRowLabel: {
    fontSize: 14,
    color: '#1A1A1A',
    fontWeight: '500',
    marginBottom: 12,
  },
  formatRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 12,
    marginBottom: 12,
  },
  formatLabel: { fontSize: 14, color: '#1A1A1A', fontWeight: '500' },
  formatToggle: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 2,
    gap: 2,
  },
  formatPill: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    minWidth: 60,
    alignItems: 'center',
  },
  formatPillActive: { backgroundColor: '#E8490F' },
  formatPillText: { fontSize: 14, fontWeight: '600', color: '#6B7280' },
  formatPillTextActive: { color: '#fff' },
  emptyText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    lineHeight: 20,
  },
  layoutTiles: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  layoutTile: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#E5E7EB',
    backgroundColor: '#fff',
  },
  layoutTileActive: { borderColor: '#E8490F', backgroundColor: '#FEF0EB' },
  layoutGrid: {
    width: 34,
    height: 34,
    flexDirection: 'column',
    gap: 3,
  },
  layoutGridRow: { flex: 1, flexDirection: 'row', gap: 3 },
  layoutCell: { flex: 1, borderRadius: 2, backgroundColor: '#D1D5DB' },
  layoutCellActive: { backgroundColor: '#E8490F' },
  layoutLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
    marginTop: 6,
  },
  layoutLabelActive: { color: '#E8490F' },

  body: { flexGrow: 0, minHeight: 200 },
  listContent: { paddingVertical: 4 },

  masterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  masterLabel: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },

  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSmall: {
    width: 18,
    height: 18,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxActive: { backgroundColor: '#E8490F', borderColor: '#E8490F' },
  checkboxDash: {
    width: 12,
    height: 2,
    borderRadius: 1,
    backgroundColor: '#fff',
  },

  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  listThumb: {
    width: 46,
    height: 46,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  thumbImg: { width: '100%', height: '100%' },
  listMeta: { flex: 1, minWidth: 0 },
  listName: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  listSize: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },

  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 10,
  },
  navArrow: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navArrowDisabled: { backgroundColor: '#F9FAFB', borderColor: '#F3F4F6' },
  paginationText: { fontSize: 13, color: '#6B7280', fontWeight: '500' },

  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 16,
  },
  button: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#fff',
    borderWidth: 1.5,
    borderColor: '#E5E7EB',
  },
  cancelText: { color: '#1A1A1A', fontSize: 14, fontWeight: '600' },
  exportButton: { backgroundColor: '#E8490F' },
  exportText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});
